import { useEffect, useState } from 'react';

import { useAnalytics } from '../../../../../common/controller/analytics/use-analytics';
import { logger } from '../../../../../common/utils/logger';
import { onBookmarkFetchFailed } from '../../analytics';

import { BookmarkState, fetchBookmark } from './fetch-bookmark';

export type UseBookmarkQueryResult = {
	loading: boolean;
	isError: boolean;
	id: string;
	bookmarkState: BookmarkState;
	url: string;
	title: string;
	description: string | undefined;
	refetch: () => void;
	lastModifierAccountId: string;
	keyPhrase: string;
	parentAdminhubBookmarkId: string;
	requestId: string | null;
};

type UseBookmarkQueryParams = {
	cloudId: string;
	query: string;
	workspaceId: string;
	product: string;
	isStandaloneBookmark?: boolean;
	applicationMode?: 'rovo' | 'unified';
};

export const useBookmarkQuery = ({
	cloudId,
	query,
	workspaceId,
	product,
	isStandaloneBookmark,
	applicationMode,
}: UseBookmarkQueryParams): UseBookmarkQueryResult => {
	const [fireAnalyticsEvent] = useAnalytics();

	const [loading, setLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [refetchCounter, setRefetchCounter] = useState(1);
	const [id, setId] = useState('');
	const [url, setUrl] = useState('');
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState<string | undefined>();
	const [bookmarkState, setBookmarkState] = useState<BookmarkState>(BookmarkState.ACTIVE);
	const [lastModifierAccountId, setLastModifierAccountId] = useState('');
	const [keyPhrase, setKeyPhrase] = useState('');
	const [parentAdminhubBookmarkId, setParentAdminhubBookmarkId] = useState('');
	const [requestId, setRequestId] = useState<string | null>(null);

	useEffect(() => {
		const callApi = async () => {
			try {
				const response = await fetchBookmark({ cloudId, keyPhrase: query }, workspaceId, product);
				const data = response.data;
				const requestId = response.requestId;

				if (
					!data ||
					(data.extensions && data.extensions.length > 0 && data.extensions[0].statusCode !== 404)
				) {
					fireAnalyticsEvent(onBookmarkFetchFailed());
					logger.error({
						error: data?.extensions || 'Bookmark Fetch Error',
						functionName: isStandaloneBookmark
							? 'useBookmarkQueryStandaloneBookmark'
							: 'useBookmarkQuery',
					});
					setIsError(true);
					return;
				}

				setId(data?.id || '');
				setUrl(data?.properties?.url || '');
				setDescription(data?.properties?.description || '');
				setTitle(data?.properties?.title || '');
				setBookmarkState(data?.properties?.bookmarkState || BookmarkState.ACTIVE);
				setLastModifierAccountId(data?.properties?.lastModifierAccountId ?? '');
				setKeyPhrase(data?.properties?.keyPhrase ?? '');
				setParentAdminhubBookmarkId(data?.properties?.parentAdminhubBookmarkId ?? '');
				setRequestId(requestId);
			} catch (error) {
				fireAnalyticsEvent(onBookmarkFetchFailed());
				logger.error({
					error,
					functionName: isStandaloneBookmark
						? 'useBookmarkQueryStandaloneBookmark'
						: 'useBookmarkQuery',
				});
				setIsError(true);
			} finally {
				setLoading(false);
			}
		};
		if (!!query && applicationMode === 'rovo') {
			setLoading(true);
			callApi();
		}
	}, [
		cloudId,
		query,
		refetchCounter,
		fireAnalyticsEvent,
		workspaceId,
		product,
		isStandaloneBookmark,
		applicationMode,
	]);

	const refetch = (): void => setRefetchCounter((counter) => counter + 1);

	return {
		loading,
		isError,
		id,
		bookmarkState,
		url,
		title,
		description,
		refetch,
		lastModifierAccountId,
		keyPhrase,
		parentAdminhubBookmarkId,
		requestId,
	};
};
